import { useEffect, useState } from "react";
import { Button, Cluster, Spinner } from "@salesapp/ui";
import { useNavigate, useParams } from "react-router";
import { useAccess } from "../../../shell/AccessProvider";
import { dashboardDesignerApi, dashboardMineApi, normalizeDashboardLayout } from "./api";
import type { DashboardTemplate } from "./api";
import { DashboardRenderer } from "./DashboardRenderer";
import "./dashboard_designer.css";
import "./designers.css";

export function DashboardViewPage({
  orgId,
  listPath = "/apps/crm/dashboard-designer",
  mode = "scoped",
}: {
  orgId: string;
  listPath?: string;
  mode?: "scoped" | "mine";
}) {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const { can } = useAccess();
  const canDesign = mode === "scoped" && (can("crm.dashboard.design", "design") || can("crm.dashboard.design"));
  const [template, setTemplate] = useState<DashboardTemplate>();
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!orgId || !dashboardId) return;
    setError("");
    const get = mode === "mine" ? dashboardMineApi.get : dashboardDesignerApi.get;
    void get(orgId, dashboardId).then(setTemplate).catch(e => setError((e as Error).message));
  }, [orgId, dashboardId, mode]);

  if (!orgId || !dashboardId) return <Spinner />;
  if (error && !template) return <p role="alert">{error}</p>;
  if (!template) return <Spinner />;

  const dashboard = template;
  const client = mode === "mine" ? dashboardMineApi : dashboardDesignerApi;
  const layout = normalizeDashboardLayout(dashboard.layout_json, dashboard.name);

  async function removeDashboard() {
    if (!orgId || !dashboardId) return;
    if (!confirm(`Delete "${dashboard.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    setError("");
    try {
      await dashboardDesignerApi.remove(orgId, dashboardId);
      navigate(listPath);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="designer">
      <Cluster justify="space-between" align="center">
        <Button
          variant="secondary"
          title="Return to the dashboards list."
          onClick={() => navigate(listPath)}
        >
          Back to dashboards
        </Button>
        {canDesign && (
          <Cluster gap={2}>
            <Button
              variant="secondary"
              title={`Open “${dashboard.name}” in the designer to change layout, charts, and sharing.`}
              onClick={() => navigate(`${listPath}/${dashboardId}/designer`)}
            >
              Edit
            </Button>
            <Button
              variant="danger"
              disabled={deleting}
              title={`Delete “${dashboard.name}”. You will be asked to confirm.`}
              onClick={() => removeDashboard()}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </Cluster>
        )}
      </Cluster>
      {error && <p role="alert">{error}</p>}
      <DashboardRenderer orgId={orgId} dashboardId={dashboard.id} layout={layout} client={client} />
    </div>
  );
}
